import React, { useMemo, useState } from 'react';
import { Sparkles, MessageCircle } from 'lucide-react';
import Button from '../common/Button';
import { t } from '../../i18n';

const librarySeed = [
  {
    id: 'cat-agua',
    categoria: 'Agua y obras hidraulicas',
    items: [
      {
        code: 'PAS 111',
        description: 'Descarga de residuos liquidos a aguas superficiales o subterraneas.',
        example: 'Descargas desde plantas de tratamiento o drenajes del proyecto.'
      },
      {
        code: 'PAS 112',
        description: 'Obras en cauces naturales.',
        example: 'Cruces de quebradas o canales con caminos o cables.'
      },
      {
        code: 'PAS 113',
        description: 'Extraccion de aridos en cauces.',
        example: 'Material para caminos internos o plataformas.'
      },
      {
        code: 'PAS 115',
        description: 'Captacion de aguas superficiales o subterraneas.',
        example: 'Uso de agua para construccion o lavado de paneles.'
      }
    ]
  },
  {
    id: 'cat-residuos',
    categoria: 'Residuos y sustancias peligrosas',
    items: [
      {
        code: 'PAS 118',
        description: 'Transporte de residuos peligrosos.',
        example: 'Aceites, solventes o residuos industriales.'
      },
      {
        code: 'PAS 119',
        description: 'Manejo de sustancias peligrosas.',
        example: 'Almacenamiento de combustibles o quimicos.'
      },
      {
        code: 'PAS 121',
        description: 'Almacenamiento de sustancias peligrosas.',
        example: 'Bodegas de combustibles o aceites.'
      }
    ]
  },
  {
    id: 'cat-flora',
    categoria: 'Flora y vegetacion',
    items: [
      {
        code: 'PAS 127',
        description: 'Corta de Alerce.',
        example: 'Muy raro en proyectos solares.'
      },
      {
        code: 'PAS 128',
        description: 'Corta de Araucaria.',
        example: 'Poco comun.'
      },
      {
        code: 'PAS 129',
        description: 'Corta o intervencion de especies vegetales protegidas.',
        example: 'Aplica en zonas con vegetacion protegida.'
      },
      {
        code: 'PAS 130',
        description: 'Intervencion en bosque nativo.',
        example: 'Aplica en zonas con vegetacion protegida.'
      }
    ]
  },
  {
    id: 'cat-patrimonio',
    categoria: 'Patrimonio cultural',
    items: [
      {
        code: 'PAS 126',
        description: 'Intervencion o afectacion de monumentos nacionales o patrimonio arqueologico.',
        example: 'Muy comun en proyectos solares del norte.'
      }
    ]
  },
  {
    id: 'cat-protegidas',
    categoria: 'Areas protegidas',
    items: [
      {
        code: 'PAS 125',
        description: 'Intervencion en areas protegidas o sitios prioritarios de conservacion.',
        example: 'Aplica si el proyecto esta cerca o dentro de zonas sensibles.'
      }
    ]
  },
  {
    id: 'cat-electrica',
    categoria: 'Infraestructura electrica',
    subtitle:
      'Aplica cuando el proyecto incluye subestacion elevadora, linea de transmision o conexion al sistema electrico.',
    items: [
      {
        code: 'PAS 140',
        description: 'Instalaciones electricas de alta tension.',
        example: 'Conexiones de alta tension asociadas al proyecto.'
      },
      {
        code: 'PAS 141',
        description: 'Subestaciones electricas.',
        example: 'Subestacion elevadora para inyeccion al sistema.'
      },
      {
        code: 'PAS 142',
        description: 'Lineas de transmision electrica.',
        example: 'Linea de evacuacion y conexion al sistema.'
      }
    ]
  }
];

const DevelopmentRegulatoryAssistant = () => {
  const [form, setForm] = useState({
    tecnologia: 'Solar FV',
    potenciaMw: 3,
    region: 'Antofagasta',
    comuna: 'Maria Elena',
    ubicacion: 'Rural',
    pregunta: ''
  });
  const [result, setResult] = useState(null);

  const summary = useMemo(() => `${form.tecnologia} • ${form.potenciaMw} MW • ${form.region}`, [form]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const generateRecommendation = () => {
    setResult({
      proyecto: summary,
      permisos: [
        {
          code: 'PAS 111',
          description: t('Descarga de residuos liquidos a aguas superficiales o subterraneas.'),
          example: 'Descargas desde plantas de tratamiento o drenajes del proyecto.'
        },
        {
          code: 'PAS 115',
          description: t('Captacion de aguas superficiales o subterraneas.'),
          example: 'Uso de agua para construccion o lavado de paneles.'
        },
        {
          code: 'PAS 126',
          description: t(
            'Intervencion o afectacion de monumentos nacionales o patrimonio arqueologico.'
          ),
          example: 'Aplicable en proyectos con potencial hallazgo arqueologico.'
        },
        {
          code: 'PAS 142',
          description: t('Lineas de transmision electrica.'),
          example: 'Linea de evacuacion y conexion al sistema.'
        }
      ]
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t('Asistente normativo PAS')}</h1>
          <p className="text-slate-600 text-sm">{t(
            'Recibe recomendaciones automaticas de RCA y permisos sectoriales segun la normativa chilena.'
          )}</p>
          <p className="text-xs text-slate-400 mt-1">{t('Consultando como super_user')}</p>
        </div>
        <div className="inline-flex items-center gap-2 text-slate-600 text-sm">
          <MessageCircle className="w-4 h-4 text-neutral-400" />{t('Basado en reglas PAS y DS vigentes')}</div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <section className="bg-white border border-slate-200 rounded-3xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-600">{t('Tecnologia')}</label>
              <select value={form.tecnologia} onChange={handleChange('tecnologia')} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2">
                <option>{t('Solar FV')}</option>
                <option>{t('Eolico')}</option>
                <option>{t('BESS')}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">{t('Potencia (MW)')}</label>
              <input type="number" value={form.potenciaMw} onChange={handleChange('potenciaMw')} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">{t('Region')}</label>
              <input value={form.region} onChange={handleChange('region')} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">{t('Comuna')}</label>
              <input value={form.comuna} onChange={handleChange('comuna')} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-slate-600">{t('Ubicacion')}</label>
              <select value={form.ubicacion} onChange={handleChange('ubicacion')} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2">
                <option>{t('Rural')}</option>
                <option>{t('Periurbano')}</option>
                <option>{t('Urbano')}</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-semibold text-slate-600">{t('Pregunta especifica (opcional)')}</label>
              <textarea
                value={form.pregunta}
                onChange={handleChange('pregunta')}
                rows={3}
                className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2"
                placeholder={t('Ej.: Que PAS necesito para un parque eolico de 20 MW en Biobio?')}
              />
            </div>
          </div>

          <Button variant="primary" icon={Sparkles} className="mt-5 w-full justify-center" onClick={generateRecommendation}>{t('Generar recomendacion')}</Button>
        </section>

        <section className="bg-white border border-slate-200 rounded-3xl p-6">
          {result ? (
            <div>
              <p className="text-sm text-slate-500">{t('Proyecto evaluado')}</p>
              <h3 className="text-xl font-bold text-slate-900 mt-1">{t(result.proyecto)}</h3>
              <div className="mt-4 space-y-3">
                {result.permisos.map((item) => (
                  <div key={item.code} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-sm font-bold text-slate-900">{t(item.code)}</p>
                    <p className="text-sm text-slate-700"><span className="font-semibold">{t('Descripcion:')}</span> {t(item.description)}</p>
                    <p className="text-xs text-slate-500 mt-1"><span className="font-semibold">{t('Ejemplo:')}</span> {t(item.example)}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[320px] grid place-items-center text-center text-slate-500">
              <div>
                <Sparkles className="w-8 h-8 mx-auto text-neutral-300 mb-2" />
                <p className="text-sm">{t('Completa la informacion del proyecto y obten la lista de')}</p>
                <p className="text-sm">{t('PAS, RCA y permisos aplicables segun normativa chilena.')}</p>
              </div>
            </div>
          )}
        </section>
      </div>

      <section className="bg-white border border-slate-200 rounded-3xl p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">{t('Biblioteca normativa PAS (extracto)')}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {librarySeed.map((entry) => (
            <article key={entry.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h3 className="font-bold text-slate-900 text-sm leading-snug">{t(entry.categoria)}</h3>
              {entry.subtitle ? <p className="text-sm text-slate-500 mt-2">{t(entry.subtitle)}</p> : null}
              <div className="mt-3 space-y-3">
                {entry.items.map((item) => (
                  <div key={item.code} className="rounded-xl border border-slate-200 bg-white p-3">
                    <p className="text-sm font-bold text-slate-900">{t(item.code)}</p>
                    <p className="text-sm text-slate-700"><span className="font-semibold">{t('Descripcion:')}</span> {t(item.description)}</p>
                    <p className="text-xs text-slate-500 mt-1"><span className="font-semibold">{t('Ejemplo:')}</span> {t(item.example)}</p>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DevelopmentRegulatoryAssistant;
